import { CheckCircle2, Copy, Check, Share2 } from "lucide-react";
import { PrimaryButton, SecondaryButton, GhostButton, IconButton } from "@/components/StandardButton";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { SectionHeader, BodyText } from "@/components/ui/typography";

interface SubmissionSuccessProps {
  trackingCode: string;
  onSubmitMore: () => void;
  brandColor?: string;
  brandColorContrast?: string;
}

export const SubmissionSuccess = ({ 
  trackingCode, 
  onSubmitMore, 
  brandColor,
  brandColorContrast 
}: SubmissionSuccessProps) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Trigger confetti on mount
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: [brandColor || '#9b87f5', '#7E69AB', '#6E59A5'],
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: [brandColor || '#9b87f5', '#7E69AB', '#6E59A5'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    frame();
  }, [brandColor]);

  const copyTrackingCode = async () => {
    try {
      await navigator.clipboard.writeText(trackingCode);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Tracking code copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the code manually",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <Card className="max-w-lg w-full">
        <CardContent className="py-12 space-y-8">
          {/* Success Icon */}
          <div className="text-center">
            <CheckCircle2 
              className="h-20 w-20 mx-auto mb-4 animate-in zoom-in duration-500" 
              style={{ color: brandColor || '#22c55e' }}
            />
            <SectionHeader className="mb-2">Thank You!</SectionHeader>
            <BodyText variant="large" className="text-muted-foreground">
              Your feedback has been submitted successfully
            </BodyText>
          </div>

          {/* Tracking Code Section */}
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Share2 className="h-4 w-4 text-primary" />
              <span>Your Tracking Code</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Save this code to check the status of your feedback later (optional)
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-background border rounded-md text-sm font-mono">
                {trackingCode}
              </code>
              <IconButton
                onClick={copyTrackingCode}
                className="flex-shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </IconButton>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="space-y-3 p-4 bg-primary/5 rounded-lg border border-primary/10">
            <h3 className="font-semibold text-sm">What happens next?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Your feedback will be reviewed by the team</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>It's completely anonymous - we can't identify you</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Your input helps improve the workplace for everyone</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <PrimaryButton 
              onClick={() => navigate(`/track/${trackingCode}`)}
              className="w-full"
              style={{ 
                backgroundColor: brandColor || undefined, 
                color: brandColorContrast 
              }}
            >
              Track Your Feedback
            </PrimaryButton>
            <SecondaryButton 
              onClick={onSubmitMore} 
              className="w-full"
            >
              Submit More Feedback
            </SecondaryButton>
            <GhostButton 
              onClick={() => window.location.href = 'https://sendfeedback.ai'} 
              className="w-full"
            >
              Done
            </GhostButton>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
